import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils.ts';
import { Check, ChevronsUpDown } from 'lucide-react';
import React from 'react';

type ComboboxProps = {
  chooseClassificationCode: (
    type: string,
    label: string,
    value: string,
  ) => void;
  label?: string;
  name: string;
  options: Array<{ label: string; reasoning?: string; value: string }>;
  suggestions?: Array<{ label: string; value: string }>;
};

function ClassificationCombobox(props: ComboboxProps) {
  const { chooseClassificationCode, label, name, options, suggestions } = props;
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState('');

  const chooseOption = (value: string) => {
    const option = options.find((option) => option.value == value);
    chooseClassificationCode(name, option?.label || '', value);
  };

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <div>
          <Button
            aria-expanded={open}
            className="h-full w-full items-start justify-between"
            role="combobox"
            variant="outline"
          >
            <div className="max-w-full flex-grow overflow-x-auto text-left">
              <span className="block whitespace-normal break-words text-left">
                {value
                  ? options.find((option) => option.value == value)?.label
                  : `Select ${label}...`}
              </span>
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </div>
      </PopoverTrigger>
      <div className="m-2 flex flex-col gap-1">
        {suggestions.length > 0 && (
          <span className="text-xs italic text-gray-500">Suggestions:</span>
        )}
        <div className="m-1 flex flex-row flex-wrap gap-2">
          {suggestions?.map((suggestion) => (
            <div
              className="relative flex flex-row gap-1"
              key={suggestion.value}
            >
              <Badge
                className="cursor-pointer rounded-full px-2 py-1 pr-8 text-xs text-gray-600"
                onClick={(e) => {
                  e.preventDefault();
                  setValue(suggestion.value);
                  chooseOption(suggestion.value);
                  setOpen(false);
                }}
                variant={suggestion.value == value ? 'default' : 'outline'}
              >
                {suggestion.label}
              </Badge>
              {suggestion.reasoning && (
                <Tooltip>
                  <TooltipTrigger>
                      <span className="p-0.2 absolute right-3 top-1/2 -translate-y-1/2 transform rounded-full border border-gray-500 px-1.5 text-xs italic text-gray-500">
                        i
                      </span>
                  </TooltipTrigger>
                  <TooltipContent className="w-96 rounded-md border border-gray-200 bg-white p-2 shadow-sm">
                    <span>{suggestion.reasoning}</span>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          ))}
        </div>
      </div>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] bg-white p-0">
        <Command
          filter={(value, search, keywords) => {
            const extendValue = value + ' ' + keywords.join(' ');
            if (extendValue.toLowerCase().includes(search.toLowerCase())) {
              return 1;
            }
            return 0;
          }}
        >
          <CommandInput placeholder={`Search ${label}...`} />
          <CommandList>
            <CommandEmpty>No {label} found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  keywords={[option.label]}
                  onSelect={(currentValue) => {
                    setValue(currentValue?.split('-')?.[0]);
                    chooseOption(currentValue?.split('-')?.[0]);
                    setOpen(false);
                  }}
                  value={option.value}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value == option.value ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="flex flex-wrap">
                    {option.value} - {option.label}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default ClassificationCombobox;
